import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { FormControl, FormHelperText, Grid, MenuItem, Select, Typography } from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'

import { variantTypes, NONE_VALUE } from '../../../../../../constants'
import { Logger, LoggerTypes } from '../../../../../../utils'
import { FormTitle, iconNames } from '../../../../../../components'
import { FlowElementBottomButtons } from '../../../FlowElementBottomButtons'
import '../../../common-elements-style.scss'

const useStyles = makeStyles((theme) =>
  createStyles({
    headerColor: {
      color: theme.palette.secondary.main,
      opacity: '0.6'
    }
  })
)

const defaultValue = {
  location_start_id: NONE_VALUE,
  location_finish_id: NONE_VALUE,
  product_id: NONE_VALUE
  // resource_impacted: NONE_VALUE
}

export const FlowElementTaskGeographyPanel = ({
  currentTaskInstance,
  locations,
  products,
  onChangeTask,
  onSaveTask,
  errors
}) => {
  const { t } = useTranslation()
  const styleClasses = useStyles()

  const location_startIdValue =
    `${currentTaskInstance?.task?.location_start_id ? currentTaskInstance.task.location_start_id : NONE_VALUE}` ||
    defaultValue.location_start_id
  const location_finishIdValue =
    `${currentTaskInstance?.task?.location_finish_id ? currentTaskInstance.task.location_finish_id : NONE_VALUE}` ||
    defaultValue.location_finish_id
  const productIdValue =
    `${currentTaskInstance?.task?.product_id ? currentTaskInstance.task.product_id : NONE_VALUE}` ||
    defaultValue.product_id
  // const resource_impactedValue =
  //   `${currentTaskInstance?.task?.resource_impacted ? currentTaskInstance.task.resource_impacted : NONE_VALUE}` ||
  //   defaultValue.resource_impacted

  const [location_start_id, setLocationStart] = useState(location_startIdValue)
  const [location_finish_id, setLocationFinish] = useState(location_finishIdValue)
  const [product_id, setProduct] = useState(productIdValue)
  // const [resource_impacted, setResourceImpacted] = useState(resource_impactedValue)

  const _getNewTask = () => {
    const newTask = {
      ...currentTaskInstance,
      task: {
        ...currentTaskInstance.task,
        id: currentTaskInstance.task.id,
        location_start_id: location_start_id === '' ? null : location_start_id,
        location_finish_id: location_finish_id === '' ? null : location_finish_id,
        product_id: product_id === '' ? null : product_id
        // resource_impacted: location_start === '' ? null : resource_impacted,
      }
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel._getNewTask()', { newTask })

    return newTask
  }

  useEffect(() => {
    const newTask = _getNewTask()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.useEffect() CHANGED', { newTask })
    onChangeTask(newTask)
  }, [
    location_start_id,
    location_finish_id,
    product_id
    // resource_impacted,
  ])

  useEffect(() => {
    setLocationStart(location_startIdValue)
    setLocationFinish(location_finishIdValue)
    setProduct(productIdValue)
  }, [currentTaskInstance])

  const onChangeLocationStart = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onChangeLocationStart()', {
      location_start_id: _value
    })
    setLocationStart(_value)
  }

  const onChangeLocationFinish = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onChangeLocationFinish()', {
      location_finish_id: _value
    })
    setLocationFinish(_value)
  }

  const onChangeProduct = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onChangeProduct()', {
      product_id: _value
    })
    setProduct(_value)
  }

  // const onChangeResourceImpacted = (e) => {
  //   e.stopPropagation()
  //   const _value = e.target.value
  //   Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onChangeResourceImpacted()', {
  //     resource_impacted: _value
  //   })
  //   setResourceImpacted(_value)
  // }

  // ---

  const onClickSave = (e) => {
    e.stopPropagation()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onClickSave()')

    onSaveTask(() => {
      // -- on completed
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskGeographyPanel.onClickSave() DONE')
    })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementTaskGeographyPanel.render()', {
    currentTaskInstance,
    locations,
    geography: {
      location_start_id,
      location_finish_id
    },
    product_id,
    errors
  })

  return (
    <div className="FlowElementTaskGeographyPanel">
      <form>
        {/* == LOCATION & MOVEMENT == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.tasks.panels.geography.position_movement.title')}
          icon={iconNames.MOVEMENT}
          iconSize={16}
        />

        <Grid container spacing={2} style={{ padding: '10px' }}>
          {/*  location_start  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ position: 'relative' }}>
            <Typography style={{ height: '25px' }}>
              {t('flowActions.tasks.panels.geography.position_movement.location_start.title')}
            </Typography>
            <FormControl error={Boolean(errors && errors?.location_start_id)} className="common_fullwidth">
              <Select
                className="common_fullwidth"
                value={location_start_id || NONE_VALUE}
                onChange={onChangeLocationStart}
                variant={variantTypes.OUTLINED}
                id="location_start-select"
              >
                <MenuItem value={NONE_VALUE}>
                  {t('flowActions.tasks.panels.geography.position_movement.location_start.default')}
                </MenuItem>
                {locations.map((location, index) => (
                  <MenuItem key={`location_start-${index}`} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.location_start_id) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/*  location_finish  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ position: 'relative' }}>
            <Typography style={{ height: '25px' }}>
              {t('flowActions.tasks.panels.geography.position_movement.location_finish.title')}
            </Typography>
            <FormControl error={Boolean(errors && errors?.location_finish_id)} className="common_fullwidth">
              <Select
                className="common_fullwidth"
                value={location_finish_id || NONE_VALUE}
                onChange={onChangeLocationFinish}
                variant={variantTypes.OUTLINED}
                id="location_finish-select"
              >
                <MenuItem value={NONE_VALUE}>
                  {t('flowActions.tasks.panels.geography.position_movement.location_finish.default')}
                </MenuItem>
                {locations.map((location, index) => (
                  <MenuItem key={`location_finish-${index}`} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.location_finish_id) || ''}</FormHelperText>
            </FormControl>
          </Grid>
          {/*  products  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ position: 'relative' }}>
            <Typography style={{ height: '25px' }}>
              {t('flowActions.tasks.panels.geography.position_movement.products.title')}
            </Typography>
            <FormControl error={Boolean(errors && errors?.product_id)} className="common_fullwidth">
              <Select
                className="common_fullwidth"
                value={product_id || NONE_VALUE}
                onChange={onChangeProduct}
                variant={variantTypes.OUTLINED}
                id="product-select"
              >
                <MenuItem value={NONE_VALUE}>
                  {t('flowActions.tasks.panels.geography.position_movement.products.default')}
                </MenuItem>
                {products.map((product, index) => (
                  <MenuItem key={`product-${index}`} value={product.id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.product_id) || ''}</FormHelperText>
            </FormControl>
          </Grid>

          {/*  resource_impacted  */}
          {/* <Grid item xs={12} sm={12} className="common_fullwidth" style={{ position: 'relative' }}>
            <Typography style={{ height: '25px' }}>
              {t('flowActions.tasks.panels.geography.position_movement.resource_impacted.title')}
            </Typography>
            <FormControl error={Boolean(errors && errors?.resource_impacted)} className="common_fullwidth">
              <Select
                className="common_fullwidth"
                value={resource_impacted || NONE_VALUE}
                onChange={onChangeResourceImpacted}
                variant={variantTypes.OUTLINED}
                id="resource_impacted-select"
              >
                <MenuItem value={NONE_VALUE}>
                  {t('flowActions.tasks.panels.geography.position_movement.resource_impacted.default')}
                </MenuItem>
                {resources.map((resource, index) => (
                  <MenuItem key={`resource_impacted-${index}`} value={resource.id}>
                    {resource.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{(errors && errors?.resource_impacted) || ''}</FormHelperText>
            </FormControl>
          </Grid> */}
        </Grid>

        <FlowElementBottomButtons onSave={onClickSave} />
      </form>
    </div>
  )
}

FlowElementTaskGeographyPanel.displayName = 'FlowElementTaskGeographyPanel'

FlowElementTaskGeographyPanel.propTypes = {
  currentTaskInstance: PropTypes.object,
  locations: PropTypes.arrayOf(PropTypes.object),
  products: PropTypes.arrayOf(PropTypes.object),
  onChangeTask: PropTypes.func.isRequired,
  onSaveTask: PropTypes.func.isRequired,
  errors: PropTypes.array
}

FlowElementTaskGeographyPanel.defaultProps = {
  currentTaskInstance: null,
  locations: [],
  products: [],
  onChangeTask: () => {},
  onSaveTask: () => {},
  errors: null
}
